import React, { useState, useEffect } from 'react';
import { ArrowRight, Plus, Trash2, RefreshCw, Upload, CornerDownRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('adminToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const RedirectManager = () => {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ source_path: '', target_path: '' });
  const [bulk, setBulk] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRedirects = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/redirects`, { headers: authHeaders() });
      const data = await res.json();
      setRedirects(data.redirects || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRedirects(); }, []);

  const handleAdd = async () => {
    if (!form.source_path.trim() || !form.target_path.trim()) {
      alert('Enter both an old path and a target path.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/redirects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setForm({ source_path: '', target_path: '' });
        fetchRedirects();
      } else {
        const err = await res.json();
        alert('Failed: ' + (err.detail || 'error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBulk = async () => {
    if (!bulk.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/redirects/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ pairs: bulk }),
      });
      const data = await res.json();
      alert(`Imported ${data.created_count} redirect(s).` + (data.errors?.length ? `\n${data.errors.length} line(s) skipped.` : ''));
      setBulk('');
      fetchRedirects();
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r) => {
    await fetch(`${API_URL}/api/redirects/${r.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ active: !r.active }),
    });
    fetchRedirects();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this redirect?')) return;
    await fetch(`${API_URL}/api/redirects/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchRedirects();
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="redirect-manager-page">
      <div className="flex items-center gap-3 mb-2">
        <CornerDownRight className="w-7 h-7 text-teal-600" />
        <h1 className="text-2xl font-bold text-slate-800">301 Redirect Manager</h1>
      </div>
      <p className="text-slate-600 mb-6 text-sm max-w-2xl">
        Point old / deleted / mistyped URLs (that still have backlinks) to the correct
        live page. Visitors and crawlers land on the right content instead of a 404,
        reclaiming the link equity.
      </p>

      {/* Add single */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add a redirect</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">Old path (from)</label>
            <input
              data-testid="redirect-source-input"
              value={form.source_path}
              onChange={(e) => setForm({ ...form, source_path: e.target.value })}
              placeholder="/old-implants-page"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
            />
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 hidden sm:block mb-2" />
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">Target path (to)</label>
            <input
              data-testid="redirect-target-input"
              value={form.target_path}
              onChange={(e) => setForm({ ...form, target_path: e.target.value })}
              placeholder="/dental-implants"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
            />
          </div>
          <button
            data-testid="redirect-add-btn"
            onClick={handleAdd}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {/* Bulk import */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-8">
        <h2 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><Upload className="w-4 h-4" /> Bulk import</h2>
        <p className="text-xs text-slate-500 mb-3">One per line, in the form <code>/old-url -&gt; /new-url</code> (also accepts comma-separated).</p>
        <textarea
          data-testid="redirect-bulk-input"
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          rows={5}
          placeholder={"/old-implants -> /dental-implants\n/dr-mund -> /our-team\n/cleaning-special -> /179-healthy-cleaning-exam-and-set-of-x-rays"}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal-500 focus:outline-none"
        />
        <button
          data-testid="redirect-bulk-btn"
          onClick={handleBulk}
          disabled={saving}
          className="mt-3 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-5 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
        >
          Import list
        </button>
      </div>

      {/* List */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800">Active redirects ({redirects.length})</h2>
        <button onClick={fetchRedirects} className="text-slate-500 hover:text-slate-700" data-testid="redirect-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {redirects.length === 0 ? (
        <p className="text-slate-400 text-sm" data-testid="redirect-empty">No redirects yet. Add your dead-URL → live-page mappings above.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="redirect-list">
          {redirects.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3 text-sm">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.active ? 'bg-green-500' : 'bg-slate-300'}`} />
              <span className="font-mono text-slate-700 truncate">{r.source_path}</span>
              <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="font-mono text-teal-700 truncate flex-1">{r.target_path}</span>
              <span className="text-xs text-slate-400 flex-shrink-0">{r.hits || 0} hits</span>
              <button onClick={() => toggleActive(r)} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 flex-shrink-0">
                {r.active ? 'Disable' : 'Enable'}
              </button>
              <button onClick={() => handleDelete(r.id)} data-testid={`redirect-delete-${r.id}`} className="text-red-500 hover:text-red-600 flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RedirectManager;
